/**
 * useAlias Hook
 * Provides organization-specific label aliases for valuation forms
 *
 * Usage:
 * const { getLabel, examples } = useAlias();
 * const title = getLabel('sales_value_title'); // Returns alias or default
 */

import { useOrganization } from '@/lib/platform/contexts/organization-context';

// Default labels (English)
export const DEFAULT_LABELS = {
  // Sales Value Section
  sales_value_title: 'Sales Value',
  sales_value_description: 'Revenue from merchandise and matchday sales.',
  merchandise_revenue: 'Merchandise Revenue',
  matchday_revenue: 'Matchday Revenue',

  // Transfer Value Section
  transfer_value_title: 'Player Sales Value',
  transfer_value_description: 'Track individual player transfers and sales.',

  // Brand Value Section
  brand_value_title: 'Brand Value Indicators',
  brand_value_description: 'Qualitative brand indicators that determine the value multiplier.',

  // Partnership Terminology
  sponsorship_module: 'Sponsorships',
  sponsor_singular: 'Sponsor',
  sponsor_plural: 'Sponsors',
  package_singular: 'Package',
  package_plural: 'Packages',
} as const;

export type LabelKey = keyof typeof DEFAULT_LABELS;

// Example suggestions for different industries
export const LABEL_EXAMPLES: Record<
  LabelKey,
  {
    sports: string; // Sportsklub (default)
    creator: string; // Influencer/Creator
    charity: string; // Nødhjælpsorganisation
    business: string; // Virksomhed / Koncern
    events: string; // Event/Konference
  }
> = {
  sales_value_title: {
    sports: 'Sales Value',
    creator: 'Creator Earnings',
    charity: 'Donation Income',
    business: 'Revenue',
    events: 'Event Revenue',
  },
  sales_value_description: {
    sports: 'Revenue from merchandise and matchday sales.',
    creator: 'Income from social media platforms and brand appearances.',
    charity: 'Donations and fundraising income.',
    business: 'Revenue from product and service sales.',
    events: 'Revenue from tickets and exhibitor sales.',
  },
  merchandise_revenue: {
    sports: 'Merchandise Revenue',
    creator: 'Income from Social Media Platforms',
    charity: 'Donations',
    business: 'Product Revenue',
    events: 'Vendor Revenue',
  },
  matchday_revenue: {
    sports: 'Matchday Revenue',
    creator: 'Appearance Fees & Merch Sales',
    charity: 'Product Sales',
    business: 'Service Revenue',
    events: 'Ticket Revenue',
  },
  transfer_value_title: {
    sports: 'Player Sales Value',
    creator: 'N/A',
    charity: 'N/A',
    business: 'Asset Sales',
    events: 'N/A',
  },
  transfer_value_description: {
    sports: 'Track individual player transfers and sales.',
    creator: 'Not applicable for creators.',
    charity: 'Not applicable for charities.',
    business: 'Track equipment and asset sales.',
    events: 'Not applicable for events.',
  },
  brand_value_title: {
    sports: 'Brand Value Indicators',
    creator: 'Personal Brand Value',
    charity: 'Mission Impact',
    business: 'Brand Strength',
    events: 'Event Brand Impact',
  },
  brand_value_description: {
    sports: 'Qualitative brand indicators that determine the value multiplier.',
    creator: 'Creator reputation metrics that affect earning potential.',
    charity: 'Mission alignment and social impact indicators.',
    business: 'Brand equity factors that influence market value.',
    events: 'Event reputation factors that drive attendance value.',
  },
  sponsorship_module: {
    sports: 'Sponsorships',
    creator: 'Partnerships',
    charity: 'Partnerships',
    business: 'Partnerships',
    events: 'Partnerships',
  },
  sponsor_singular: {
    sports: 'Sponsor',
    creator: 'Brand',
    charity: 'Partner',
    business: 'Client',
    events: 'Partner',
  },
  sponsor_plural: {
    sports: 'Sponsors',
    creator: 'Brands',
    charity: 'Partners',
    business: 'Clients',
    events: 'Partners',
  },
  package_singular: {
    sports: 'Package',
    creator: 'Deal',
    charity: 'Package',
    business: 'Package',
    events: 'Package',
  },
  package_plural: {
    sports: 'Packages',
    creator: 'Deals',
    charity: 'Packages',
    business: 'Packages',
    events: 'Packages',
  },
};

interface LabelAliases {
  [key: string]: string | null | undefined;
}

export function useAlias() {
  const { organization } = useOrganization();

  // Get label aliases from organization (with type safety)
  const aliases = (organization?.label_aliases || {}) as LabelAliases;

  /**
   * Get label text - returns alias if set, otherwise returns default
   * @param key - Label key
   * @returns Label text (alias or default)
   */
  const getLabel = (key: LabelKey): string => {
    // Check if there's a custom alias
    const alias = aliases[key];

    // Return alias if it exists and is not empty, otherwise return default
    if (alias && typeof alias === 'string' && alias.trim() !== '') {
      return alias.trim();
    }

    // Return default label
    return DEFAULT_LABELS[key];
  };

  /**
   * Check if a label has been customized
   * @param key - Label key
   * @returns true if customized, false if using default
   */
  const isCustomized = (key: LabelKey): boolean => {
    const alias = aliases[key];
    return !!(alias && typeof alias === 'string' && alias.trim() !== '');
  };

  /**
   * Get all examples for a specific label
   * @param key - Label key
   * @returns Object with examples for each industry
   */
  const getExamples = (key: LabelKey) => {
    return LABEL_EXAMPLES[key];
  };

  return {
    getLabel,
    isCustomized,
    getExamples,
    examples: LABEL_EXAMPLES,
    defaults: DEFAULT_LABELS,
  };
}
